import { BaseSyncOperation } from "@/store/sync/operations/BaseSyncOperation";
import { OptimisticSyncUpdate, SyncCustomErrorData, SyncOperationKind } from "@/store/sync/types";
import { IProvideFeedbackOperation } from "@/store/sync/operations/types";
import { SyncErrorHandlingType } from "@/store/sync/operations/errors/SyncError";
import { FeedbackModelData, FeedbackUpsertedUpdateValue } from "@/store/feedback/types";
import { generateDefaultOwnerScopes } from "../helpers/common";
import { uuidModule } from "@/modules/uuid";

export class ProvideFeedbackOperation extends BaseSyncOperation<IProvideFeedbackOperation> {
  get operationKind(): SyncOperationKind {
    return "PROVIDE_FEEDBACK";
  }

  async generateOptimisticUpdates(): Promise<OptimisticSyncUpdate<FeedbackModelData>[]> {
    const optimisticUpdates: OptimisticSyncUpdate<FeedbackModelData>[] = [];

    const feedbackUpsertedSyncUpdate = this.generateProvideFeedbackSyncUpdate();
    if (feedbackUpsertedSyncUpdate) {
      optimisticUpdates.push(feedbackUpsertedSyncUpdate);
    }

    return optimisticUpdates;
  }

  async triggerRecompute() {
    // No specific recompute needed for feedback
  }

  handleInvalidError(_errorData: SyncCustomErrorData): void {
    this.triggerToast("Failed to submit feedback. Please try again.");
  }

  handlePermissionDeniedError(_errorData: SyncCustomErrorData): void {
    this.triggerToast("You don't have permission to submit feedback.");
  }

  handleTransientError(_errorData: SyncCustomErrorData): void {
    this.triggerToast(
      "Failed to submit feedback. Please try again.",
      SyncErrorHandlingType.RetryWithLimit
    );
  }

  handleUnknownError(_errorData: SyncCustomErrorData): void {
    this.triggerToast("Failed to submit feedback. Please try again.");
  }

  protected generateProvideFeedbackSyncUpdate() {
    const feedbackId = uuidModule.generate();

    // Ensure associated_context is a proper Record<string, unknown>
    const associatedContext: Record<string, unknown> =
      this.payload.associated_context && typeof this.payload.associated_context === "object"
        ? (this.payload.associated_context as Record<string, unknown>)
        : {};

    const feedbackValue: FeedbackUpsertedUpdateValue = {
      model_id: feedbackId,
      model_kind: "FEEDBACK",
      model_version: 0,
      model_data: {
        space_account_id: this.store.spaceAccounts.myPersonalSpaceAccountId,
        feedback_kind: this.payload.feedback_kind,
        associated_model_id: this.payload.associated_model_id ?? null,
        associated_model_kind: this.payload.associated_model_kind ?? null,
        associated_context: associatedContext,
        feedback_responded_at: new Date().toISOString(),
        feedback_response: this.payload.feedback_response_kind || null,
        feedback_selected_suggestions: this.payload.feedback_selected_suggestions ?? [],
        feedback_user_text: this.payload.feedback_user_text ?? null,
      },
      model_scopes: [generateDefaultOwnerScopes({ store: this.store })],
    };

    const feedbackUpsertedSyncUpdate: OptimisticSyncUpdate<FeedbackModelData> = {
      optimistic_update_id: uuidModule.generate(),
      locally_committed_at: this.committedAt,
      kind: "UPSERTED",
      value: feedbackValue,
    };

    return feedbackUpsertedSyncUpdate;
  }
}
